import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Briefcase, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  ArrowRight,
  Wallet,
  Gem,
  Gift,
  User,
  Award,
  Scissors,
  ShoppingBag,
  Watch,
  Smartphone,
  Key,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

// Leather product types
const PRODUCT_TYPES = [
  { value: 'borsa', label: 'Borsa / Borsetta', icon: ShoppingBag },
  { value: 'portafoglio', label: 'Portafoglio', icon: Wallet },
  { value: 'cintura', label: 'Cintura', icon: Scissors },
  { value: 'portacarte', label: 'Portacarte / Card holder', icon: Wallet },
  { value: 'portachiavi', label: 'Portachiavi', icon: Key },
  { value: 'portadocumenti', label: 'Portadocumenti / Organizer', icon: BookOpen },
  { value: 'pochette', label: 'Pochette / Clutch', icon: ShoppingBag },
  { value: 'zaino', label: 'Zaino in pelle', icon: Briefcase },
  { value: 'tracolla', label: 'Borsa a tracolla', icon: ShoppingBag },
  { value: 'valigetta', label: 'Valigetta / Cartella', icon: Briefcase },
  { value: 'custodia', label: 'Custodia (telefono/tablet/occhiali)', icon: Smartphone },
  { value: 'cinturino', label: 'Cinturino orologio', icon: Watch },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Leather/Material types
const MATERIALS = [
  { value: 'vacchetta', label: 'Pelle vacchetta', icon: Star },
  { value: 'vitello', label: 'Pelle di vitello', icon: Star },
  { value: 'nabuk', label: 'Nabuk', icon: Sparkles },
  { value: 'cuoio', label: 'Cuoio toscano', icon: Heart },
  { value: 'coccodrillo', label: 'Pelle di coccodrillo', icon: Gem },
  { value: 'struzzo', label: 'Pelle di struzzo', icon: Gem },
  { value: 'pitone', label: 'Pelle di pitone', icon: Gem },
  { value: 'saffiano', label: 'Pelle saffiano', icon: Briefcase },
  { value: 'vegetale', label: 'Pelle conciata al vegetale', icon: Heart },
  { value: 'vegan', label: 'Pelle vegana / Eco-leather', icon: Heart },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Colors
const COLORS = [
  { value: 'nero', label: 'Nero', icon: Wallet },
  { value: 'marrone', label: 'Marrone', icon: Wallet },
  { value: 'cognac', label: 'Cognac / Cuoio', icon: Star },
  { value: 'miele', label: 'Miele / Naturale', icon: Star },
  { value: 'blu', label: 'Blu navy', icon: Wallet },
  { value: 'bordeaux', label: 'Bordeaux', icon: Gem },
  { value: 'verde', label: 'Verde', icon: Heart },
  { value: 'rosso', label: 'Rosso', icon: Heart },
  { value: 'bicolore', label: 'Bicolore', icon: Sparkles },
  { value: 'personalizzato', label: 'Colore personalizzato', icon: Sparkles },
];

// Personalization options
const PERSONALIZATIONS = [
  { value: 'iniziali', label: 'Iniziali incise/impresse', icon: User },
  { value: 'nome', label: 'Nome completo', icon: User },
  { value: 'logo', label: 'Logo aziendale', icon: Briefcase },
  { value: 'dedica', label: 'Dedica / Frase', icon: Heart },
  { value: 'data', label: 'Data speciale', icon: Gift },
  { value: 'disegno', label: 'Disegno personalizzato', icon: Sparkles },
  { value: 'nessuna', label: 'Nessuna personalizzazione', icon: Wallet },
];

// Occasion
const OCCASIONS = [
  { value: 'uso_personale', label: 'Uso personale / Quotidiano', icon: User },
  { value: 'regalo_lui', label: 'Regalo per lui', icon: Gift },
  { value: 'regalo_lei', label: 'Regalo per lei', icon: Gift },
  { value: 'matrimonio', label: 'Matrimonio / Cerimonia', icon: Heart },
  { value: 'laurea', label: 'Laurea', icon: Award },
  { value: 'compleanno', label: 'Compleanno', icon: Gift },
  { value: 'business', label: 'Business / Professionale', icon: Briefcase },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-100', label: '€50 - €100', description: 'Piccola pelletteria' },
  { value: '100-200', label: '€100 - €200', description: 'Accessori standard' },
  { value: '200-400', label: '€200 - €400', description: 'Pelle pregiata' },
  { value: '400-700', label: '€400 - €700', description: 'Alta pelletteria' },
  { value: '700-1200', label: '€700 - €1.200', description: 'Borse artigianali' },
  { value: '1200+', label: '€1.200+', description: 'Lusso / Pelli esotiche' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: '2months', label: '2 Mesi', description: 'Progetti complessi' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example products
const LEATHER_EXAMPLES = [
  {
    idea: "Borsa da lavoro elegante per mia moglie",
    result: "Tote bag in pelle vacchetta cognac con manici e tracolla, tasca interna per laptop 13 pollici, cuciture a mano",
    image: "https://images.unsplash.com/photo-1637759292654-a12cb2be085e?w=400&h=300&fit=crop",
    category: "Borse"
  },
  {
    idea: "Portafoglio minimalista con iniziali per regalo",
    result: "Bifold in pelle vegetale marrone con iniziali impresse a caldo, 6 slot carte, tasca banconote",
    image: "https://images.pexels.com/photos/4452393/pexels-photo-4452393.jpeg?w=400&h=300&fit=crop",
    category: "Portafogli"
  },
  {
    idea: "Cintura artigianale per abito elegante",
    result: "Cintura in cuoio toscano nero, fibbia in ottone anticato, larghezza 3.5cm, cuciture laterali a vista",
    image: "https://images.unsplash.com/photo-1664286074176-5206ee5dc878?w=400&h=300&fit=crop",
    category: "Cinture"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho commissionato una borsa per mia moglie con le sue iniziali. La qualità della pelle e delle cuciture è straordinaria. Un regalo che ha apprezzato tantissimo!",
    author: "Marco B.",
    location: "Milano"
  },
  {
    text: "Cercavo un portafoglio unico per il mio fidanzato. L'artigiano ha realizzato esattamente quello che avevo in mente, con una dedica nascosta all'interno. Perfetto!",
    author: "Giulia T.",
    location: "Firenze"
  },
  {
    text: "La valigetta da lavoro che mi hanno realizzato è un capolavoro. Pelle di altissima qualità, funzionale e bellissima. La uso tutti i giorni con orgoglio.",
    author: "Roberto C.",
    location: "Roma"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Che tipo di articoli in pelle posso richiedere?",
    answer: "Puoi richiedere qualsiasi articolo di pelletteria: borse, portafogli, cinture, portacarte, portachiavi, custodie, zaini, valigette, cinturini per orologi e molto altro. Gli artigiani italiani possono realizzare praticamente qualsiasi oggetto in pelle su misura."
  },
  {
    question: "Quali tipi di pelle sono disponibili?",
    answer: "I nostri artigiani lavorano con diverse pelli pregiate: vacchetta, vitello, cuoio toscano conciato al vegetale, nabuk, saffiano. Su richiesta sono disponibili anche pelli esotiche come coccodrillo, struzzo e pitone, oltre a opzioni eco-friendly e pelle vegana."
  },
  {
    question: "Posso personalizzare il mio articolo?",
    answer: "Assolutamente sì! Puoi richiedere iniziali incise o impresse a caldo, nomi, dediche, date speciali, loghi aziendali o disegni personalizzati. La personalizzazione è inclusa in molti articoli o disponibile come opzione aggiuntiva."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un articolo?",
    answer: "I tempi variano in base alla complessità: un portacarte semplice richiede circa 1 settimana, un portafoglio 2-3 settimane, una borsa strutturata 3-6 settimane. L'artigiano ti fornirà una stima precisa con la sua proposta."
  },
  {
    question: "La pelle italiana è davvero migliore?",
    answer: "La pelletteria italiana è rinomata nel mondo per la qualità delle pelli (soprattutto il cuoio toscano conciato al vegetale), le tecniche di lavorazione tradizionali tramandate da generazioni e l'attenzione maniacale ai dettagli. Un articolo artigianale italiano dura decenni."
  }
];

export default function PelletteriaRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    product_type: '',
    material: '',
    color: '',
    personalization: '',
    occasion: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Pelletteria su Misura | Artigiani Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `leather-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi l\'articolo che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_leather_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const materialLabel = MATERIALS.find(m => m.value === formData.material)?.label || '';
      const colorLabel = COLORS.find(c => c.value === formData.color)?.label || '';
      const personalizationLabel = PERSONALIZATIONS.find(p => p.value === formData.personalization)?.label || '';
      const occasionLabel = OCCASIONS.find(o => o.value === formData.occasion)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (productLabel) details.push(`👜 Tipo articolo: ${productLabel}`);
      if (materialLabel) details.push(`🐄 Materiale: ${materialLabel}`);
      if (colorLabel) details.push(`🎨 Colore: ${colorLabel}`);
      if (personalizationLabel) details.push(`✨ Personalizzazione: ${personalizationLabel}`);
      if (occasionLabel) details.push(`🎁 Occasione: ${occasionLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} in pelle su misura`.trim() || 'Articolo in pelle su misura',
        description: fullDescription,
        category: 'Pelletteria',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('leather-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Pelletteria su Misura | Borse, Portafogli, Cinture Artigianali | Rarifex</title>
        <meta name="description" content="Commissiona articoli in pelle su misura da artigiani italiani. Borse, portafogli, cinture, accessori personalizzati. Pelle italiana di alta qualità, cuciture a mano. Gratuito e senza impegno." />
        <meta name="keywords" content="pelletteria su misura, borsa artigianale, portafoglio personalizzato, cintura italiana, pelle artigianale, cuoio toscano, Made in Italy, accessori pelle" />
        <link rel="canonical" href="https://www.rarifex.com/pelletteria-su-misura" />
        <meta property="og:title" content="Pelletteria su Misura | Artigiani Italiani | Rarifex" />
        <meta property="og:description" content="Commissiona articoli in pelle su misura da artigiani italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/pelletteria-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-amber-50/50 via-white to-orange-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Briefcase className="w-4 h-4" />
                    Pelletteria Artigianale
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Pelle italiana{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">
                      lavorata per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Borse, portafogli, cinture e accessori su misura.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Artigiani italiani creano il tuo articolo in pelle con tecniche tradizionali, cuciture a mano e personalizzazioni uniche.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Cuoio toscano</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <Card id="leather-form" className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold text-slate-800">Descrivi il tuo articolo</h2>
                      <p className="text-slate-500 text-sm mt-1">Ricevi proposte da artigiani pellettieri</p>
                    </div>
                    
                    {/* Title */}
                    <div>
                      <Label className="text-slate-700">Titolo progetto (opzionale)</Label>
                      <Input
                        placeholder="Es: Borsa da lavoro, Portafoglio per regalo..."
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label className="text-slate-700">Descrivi cosa desideri *</Label>
                      <Textarea
                        placeholder="Descrivi l'articolo in pelle che vorresti: tipo, dimensioni, colore, eventuali tasche o scomparti, personalizzazioni desiderate..."
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="mt-1 min-h-[100px]"
                        required
                      />
                    </div>
                    
                    {/* Product Type */}
                    <div>
                      <Label className="text-slate-700">Tipo di articolo</Label>
                      <select
                        value={formData.product_type}
                        onChange={(e) => handleInputChange('product_type', e.target.value)}
                        className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Seleziona...</option>
                        {PRODUCT_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Material & Color Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Tipo di pelle</Label>
                        <select
                          value={formData.material}
                          onChange={(e) => handleInputChange('material', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {MATERIALS.map(mat => (
                            <option key={mat.value} value={mat.value}>{mat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Colore</Label>
                        <select
                          value={formData.color}
                          onChange={(e) => handleInputChange('color', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {COLORS.map(color => (
                            <option key={color.value} value={color.value}>{color.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Personalization & Occasion Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Personalizzazione</Label>
                        <select
                          value={formData.personalization}
                          onChange={(e) => handleInputChange('personalization', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {PERSONALIZATIONS.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Occasione</Label>
                        <select
                          value={formData.occasion}
                          onChange={(e) => handleInputChange('occasion', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {OCCASIONS.map(occ => (
                            <option key={occ.value} value={occ.value}>{occ.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Budget & Timeline Row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700">Budget</Label>
                        <select
                          value={formData.budget}
                          onChange={(e) => handleInputChange('budget', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {BUDGET_OPTIONS.map(budget => (
                            <option key={budget.value} value={budget.value}>{budget.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Tempistiche</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(timeline => (
                            <option key={timeline.value} value={timeline.value}>{timeline.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Image Upload */}
                    <div>
                      <Label className="text-slate-700">Immagini di riferimento (opzionale)</Label>
                      <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-amber-300 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          id="leather-images"
                          disabled={uploadingFile}
                        />
                        <label htmlFor="leather-images" className="cursor-pointer flex flex-col items-center gap-2">
                          {uploadingFile ? (
                            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                          ) : (
                            <Upload className="w-8 h-8 text-slate-400" />
                          )}
                          <span className="text-sm text-slate-500">
                            {uploadingFile ? 'Caricamento...' : 'Clicca per caricare (max 5 immagini)'}
                          </span>
                        </label>
                      </div>
                      
                      {/* Uploaded images preview */}
                      {formData.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.attachments.map((url, index) => (
                            <div key={index} className="relative w-16 h-16 rounded-lg overflow-hidden group">
                              <img src={url} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <X className="w-5 h-5 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Submit Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-5 h-5 mr-2" />
                      )}
                      {loading ? 'Invio in corso...' : 'Ricevi proposte gratis'}
                    </Button>
                    
                    <p className="text-xs text-slate-400 text-center">
                      Gratuito e senza impegno. Riceverai proposte entro 24-48 ore.
                    </p>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come funziona
              </h2>
              <p className="text-lg text-slate-600">
                Dall'idea all'articolo in pelle in 4 semplici passi
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Racconta l\'articolo che desideri: tipo, pelle, colore, personalizzazioni' },
                { icon: Users, title: 'Ricevi', desc: 'Gli artigiani pellettieri interessati ti inviano le loro proposte' },
                { icon: CheckCircle, title: 'Scegli', desc: 'Valuta qualità, preventivi e portfolio per scegliere il migliore' },
                { icon: Heart, title: 'Ammira', desc: 'Segui la lavorazione e ricevi il tuo articolo unico' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <div className="text-sm font-semibold text-amber-600 mb-1">Passo {index + 1}</div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Esempi di articoli realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dall'idea all'articolo in pelle finito
              </p>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {LEATHER_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img src={example.image} alt={example.category} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {example.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="mb-3">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">L'idea</div>
                      <p className="text-slate-700 font-medium">{example.idea}</p>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Il risultato</div>
                      <p className="text-slate-600 text-sm">{example.result}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa dicono i nostri clienti
              </h2>
            </div>
            
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-0">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold">
                      {testimonial.author[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{testimonial.author}</div>
                      <div className="text-sm text-slate-500">{testimonial.location}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  Domande frequenti
                </h2>
              </div>
              
              <div className="space-y-3">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index} 
                    className={`overflow-hidden transition-all cursor-pointer ${expandedFaq === index ? 'ring-2 ring-amber-200' : ''}`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800 pr-4">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-4 pb-4 text-slate-600 border-t border-slate-100 pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-amber-600 to-orange-600">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <Briefcase className="w-16 h-16 mx-auto mb-6 text-white/90" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pronto a creare il tuo articolo in pelle?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Descrivi la tua idea e ricevi proposte da artigiani pellettieri italiani. Gratuito e senza impegno.
              </p>
              <Button 
                size="lg" 
                className="bg-white text-amber-600 hover:bg-amber-50 font-semibold text-lg px-8 py-6 h-auto shadow-xl"
                onClick={scrollToForm}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Inizia ora
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Accedi per inviare la richiesta</h3>
            <p className="text-slate-600 mb-6">
              Per ricevere proposte dagli artigiani devi accedere o creare un account gratuito.
            </p>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAuthPrompt(false)}
              >
                Annulla
              </Button>
              <Button 
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/login?redirect=/pelletteria-su-misura')}
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
